<template>
  <div class="invoice-page">
    <!-- Loading Indicator -->
    <div v-if="loading" class="loading-container">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p>Please wait ...</p>
    </div>

    <!-- Invoice Content -->
    <div v-else>
      <v-card-title>Invoice</v-card-title>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <v-btn small color="primary" @click="openPaymentAllocation">
          <v-icon small left>mdi-plus</v-icon> Add Payment
        </v-btn>
        <v-btn small color="primary" @click="showAddChargeDialog = true">
          <v-icon small left>mdi-plus</v-icon> Add Charge
        </v-btn>
        <v-btn small color="primary" @click="openAddCreditDialog">
          <v-icon small left>mdi-currency-usd</v-icon> Add Credit
        </v-btn>
      </div>

      <!-- Invoice Body -->
      <div class="invoice-container">
        <div class="invoice-header">
          <div class="address-section">
            <p><strong>To:</strong> {{ tenantname }}</p>
            <p><strong>Street:</strong> {{ tenantAddress.street || 'N/A' }}</p>
            <p><strong>City:</strong> {{ tenantAddress.city || 'N/A' }}</p>
            <p><strong>Postal Code:</strong> {{ tenantAddress.postalCode || 'N/A' }}</p>
          </div>

          <div class="logo-section">
            <img src="../assets/logo.png" alt="Company Logo" class="company-logo" />
          </div>

          <div class="invoice-info-section">
            <p><strong>Invoice Number:</strong> {{ invoiceNumber }}</p>
            <p><strong>Date:</strong> {{ formatDate(currentDate) }}</p>
            <p><strong>Due Date:</strong> {{ formatDate(dueDate) }}</p>
            <p><strong>Payment Reference:</strong> {{ leaseReference }}</p>
          </div>
        </div>

        <!-- Transaction Table with Balance Column -->
        <div class="invoice-details">
          <table class="invoice-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description</th>
                <th>VAT</th>
                <th>Amount Excl</th>
                <th>Balance</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in sortedEntries" :key="entry.id || entry.type">
                <td>{{ entry.date }}</td>
                <td>{{ entry.description }}</td>
                <td>R 0.00</td>
                <td>{{ entry.amountFormatted }}</td>
                <td>{{ entry.balanceFormatted }}</td>
                <td>
                  <v-icon v-if="entry.type !== 'balance'" small @click="editRow(entry)" class="edit-icon">mdi-pencil</v-icon>
                  <v-icon v-if="entry.type !== 'balance'" small @click="deleteRow(entry)" class="delete-icon">mdi-delete</v-icon>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Bank Details and Total Due -->
        <div class="bank-and-summary">
          <div class="bank-details">
            <p><strong>Account Name:</strong> {{ bankDetails.name || 'N/A' }}</p>
            <p><strong>Account Number:</strong> {{ bankDetails.number || 'N/A' }}</p>
            <p><strong>Bank Name:</strong> {{ bankDetails.bank || 'N/A' }}</p>
            <p><strong>Account Type:</strong> {{ bankDetails.type || 'N/A' }}</p>
            <p><strong>Branch Code:</strong> {{ bankDetails.branchCode || 'N/A' }}</p>
          </div>

          <div class="summary-details">
            <p><strong>Rent Amount:</strong> R {{ rentAmount }}</p>
            <p><strong>Total Due:</strong> {{ totalDue }}</p>
          </div>
        </div>

        <!-- ========== AGING ANALYSIS ========== -->
        <v-divider class="my-6"></v-divider>
        <div class="aging-section">
          <h3 class="text-h6 font-weight-bold mb-4">Ageing Analysis</h3>
          <v-row>
            <v-col cols="6" md="2" lg>
              <v-card outlined class="pa-3 text-center" height="100">
                <div class="text-subtitle-2 grey--text">Current</div>
                <div class="text-subtitle-1 font-weight-bold text-no-wrap">
                  {{ formatCurrency(agingBuckets.current) }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" md="2" lg>
              <v-card outlined class="pa-3 text-center" height="100">
                <div class="text-subtitle-2 grey--text">0 - 30 days</div>
                <div class="text-subtitle-1 font-weight-bold text-no-wrap">
                  {{ formatCurrency(agingBuckets.days30) }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" md="2" lg>
              <v-card outlined class="pa-3 text-center" height="100">
                <div class="text-subtitle-2 grey--text">31 - 60 days</div>
                <div class="text-subtitle-1 font-weight-bold text-no-wrap">
                  {{ formatCurrency(agingBuckets.days60) }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" md="2" lg>
              <v-card outlined class="pa-3 text-center" height="100">
                <div class="text-subtitle-2 grey--text">61 - 90 days</div>
                <div class="text-subtitle-1 font-weight-bold text-no-wrap">
                  {{ formatCurrency(agingBuckets.days90) }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="6" md="2" lg>
              <v-card outlined class="pa-3 text-center" height="100">
                <div class="text-subtitle-2 grey--text">90+ days</div>
                <div class="text-subtitle-1 font-weight-bold text-no-wrap">
                  {{ formatCurrency(agingBuckets.days90plus) }}
                </div>
              </v-card>
            </v-col>
          </v-row>
        </div>
        <!-- =================================== -->
      </div>

      <!-- Buttons Outside Invoice -->
      <div class="buttons-container">
        <v-btn @click="generatePDF" outlined color="primary" class="mr-2">Generate PDF</v-btn>
        <v-btn @click="sendInvoiceByEmail" outlined color="primary">Send Invoice by Email</v-btn>
      </div>
    </div>

    <!-- ========== ADD PAYMENT DIALOG (Statement logic) ========== -->
    <v-dialog v-model="showAddPaymentDialog" max-width="1200" persistent>
      <v-card>
        <v-card-title class="headline teal darken-1 white--text">
          <v-icon left color="white">mdi-bank-transfer-in</v-icon>
          {{ editingPaymentId ? 'Edit' : 'Add' }} Payment – {{ leaseReference }}
        </v-card-title>
        <v-card-text class="pt-6">
          <v-row class="px-6">
            <v-col cols="12" sm="4">
              <v-text-field v-model.number="paymentAlloc.amount" label="Payment Amount Received" prefix="R"
                type="number" step="0.01" outlined dense placeholder="0.00" autofocus
                @input="onPaymentAmountChange" />
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field v-model="paymentAlloc.date" label="Payment Date" type="date" outlined dense
                @input="onPaymentDateChange" />
              <div v-if="isFuturePaymentDate" class="text-caption grey--text text--darken-1 mt-1 d-flex align-center">
                <v-icon small color="blue" class="mr-1">mdi-calendar-clock</v-icon>
                Future payment recorded (post-dated or advance)
              </div>
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field :value="paymentAlloc.reference" label="Payment Reference" outlined dense readonly
                hint="Automatically generated" persistent-hint />
            </v-col>
          </v-row>

          <div class="px-6 mt-4">
            <v-alert dense outlined :color="balanceBeforePayment >= 0 ? 'error' : 'success'" class="mb-4">
              <strong>
                Balance before this payment:
                <span class="text-h6 font-weight-bold ml-2">
                  R {{ Math.abs(balanceBeforePayment).toFixed(2) }}
                </span>
                <span v-if="balanceBeforePayment >= 0" class="error--text"> (Outstanding)</span>
                <span v-else class="success--text"> (Credit)</span>
              </strong>
            </v-alert>
          </div>

          <div v-if="isEditingExistingPaymentWithAllocations" class="px-6 mb-4">
            <v-alert dense type="info" outlined class="mb-4">
              <strong>Editing payment with existing allocations</strong><br>
              You can now modify, remove, or add new allocations.
            </v-alert>
          </div>

          <div class="d-flex justify-space-between align-center mb-4 px-6">
            <h3>
              {{ isEditingExistingPaymentWithAllocations ? 'Current Allocations' : 'Available Charges' }}
              ({{ paymentAlloc.allocations.length }} items)
            </h3>
            <div>
              <v-btn small color="primary" @click="smartAllocate" class="mr-2" :disabled="!paymentAlloc.amount">
                <v-icon left small>mdi-refresh</v-icon> Smart Allocate
              </v-btn>
              <v-btn small color="primary" @click="showAddChargeInAllocation = true">
                <v-icon left small>mdi-plus</v-icon> Add New Charge
              </v-btn>
            </div>
          </div>

          <v-simple-table dense class="mx-6">
            <thead>
              <tr>
                <th>Date</th>
                <th>Description (editable)</th>
                <th class="text-right">Outstanding</th>
                <th class="text-right">Allocate</th>
                <th width="60"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(alloc, i) in paymentAlloc.allocations" :key="alloc.chargeId || i"
                :class="{ 'grey lighten-3': alloc.remaining <= 0.01 && alloc.amount <= 0.01 }">
                <td>{{ formatDate(alloc.date) }}</td>
                <td>
                  <v-text-field v-model="alloc.description" dense hide-details outlined class="mt-1"
                    style="width:340px" :disabled="alloc.remaining <= 0.01 && alloc.amount <= 0.01" />
                </td>
                <td class="text-right font-weight-bold">
                  R {{ alloc.remaining.toFixed(2) }}
                  <v-icon v-if="alloc.remaining <= 0.01" color="success" small class="ml-2">mdi-check-circle</v-icon>
                </td>
                <td>
                  <div class="d-flex align-center">
                    <v-text-field v-model.number="alloc.amount" type="number" step="0.01" dense hide-details outlined
                      prefix="R" class="mt-1" style="width:140px" @input="onAllocationInput(i)"
                      :disabled="alloc.remaining <= 0.01 && alloc.previouslyAllocated <= 0.01" />
                    <span v-if="alloc.previouslyAllocated > 0.01" class="ml-3 grey--text caption">
                      (R{{ alloc.previouslyAllocated.toFixed(2) }} prev.)
                    </span>
                    <v-icon v-if="alloc.amount > 0 && alloc.remaining - alloc.amount <= 0.01" color="success"
                      class="ml-3">mdi-check-bold</v-icon>
                  </div>
                  <div v-if="alloc.previouslyAllocated > 0.01" class="text-caption grey--text mt-1">
                    Previously allocated from this payment
                  </div>
                </td>
                <td class="text-center">
                  <v-btn icon small color="error" @click="removeAllocation(i)"
                    :disabled="alloc.previouslyAllocated > 0.01 && alloc.amount >= alloc.previouslyAllocated">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-expand-transition>
            <div v-if="showAddChargeInAllocation" class="pa-4 mx-6 mt-4 grey lighten-4 rounded">
              <v-row>
                <v-col cols="12" sm="4">
                  <v-select v-model="newChargeInAllocation.chargeType" :items="chargeTypeOptions" item-text="text"
                    item-value="value" label="Charge Type *" outlined dense></v-select>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model.number="newChargeInAllocation.amount" label="Amount *" prefix="R"
                    type="number" step="0.01" outlined dense></v-text-field>
                </v-col>
                <v-col cols="12" sm="4">
                  <v-text-field v-model="newChargeInAllocation.date" label="Date *" type="date" outlined
                    dense></v-text-field>
                </v-col>
              </v-row>
              <v-row>
                <v-col cols="12">
                  <v-text-field v-model="newChargeInAllocation.description" label="Description (optional)" outlined
                    dense></v-text-field>
                </v-col>
              </v-row>
              <div class="d-flex justify-end mt-2">
                <v-btn text @click="showAddChargeInAllocation = false">Cancel</v-btn>
                <v-btn color="primary" @click="addChargeInAllocation">Add Charge</v-btn>
              </div>
            </div>
          </v-expand-transition>

          <div class="mt-6 pa-4 mx-6 rounded"
            :class="finalBalanceAfterPayment >= 0 ? 'green lighten-5' : 'red lighten-5'">
            <div class="d-flex justify-space-between"><strong>Payment Received:</strong> <strong>R {{
              (paymentAlloc.amount || 0).toFixed(2) }}</strong></div>
            <div class="d-flex justify-space-between"><strong>Allocated to charges:</strong> <strong>R {{
              totalAllocated.toFixed(2) }}</strong></div>
            <div class="d-flex justify-space-between mt-2">
              <strong>Unallocated:</strong>
              <strong
                :class="unallocatedAmount < -0.01 ? 'error--text font-weight-bold' : (unallocatedAmount > 0.01 ? 'grey--text' : 'success--text')">
                R {{ Math.abs(unallocatedAmount).toFixed(2) }}
                <span v-if="unallocatedAmount < -0.01">(Over-allocated!)</span>
                <span v-else-if="unallocatedAmount > 0.01">(Available)</span>
                <span v-else>(Fully allocated)</span>
              </strong>
            </div>
            <div class="d-flex justify-space-between mt-4">
              <strong>Balance after this payment:</strong>
              <strong :class="finalBalanceAfterPayment >= 0 ? 'success--text' : 'error--text'">
                R {{ Math.abs(finalBalanceAfterPayment).toFixed(2) }}
                <span v-if="finalBalanceAfterPayment > 0.01" class="success--text font-weight-bold"> Credit</span>
                <span v-else-if="finalBalanceAfterPayment < -0.01" class="error--text font-weight-bold"> Owing</span>
                <span v-else class="success--text font-weight-bold"> Zero</span>
              </strong>
            </div>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelPaymentDialog">Cancel</v-btn>
          <v-btn color="teal" dark @click="savePaymentAllocation"
            :disabled="!paymentAlloc.amount || !paymentAlloc.date || savingPayment || unallocatedAmount < -0.01"
            :loading="savingPayment">
            {{ editingPaymentId ? 'Update' : 'Save' }} Payment
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ========== ADD CHARGE DIALOG (Statement logic) ========== -->
    <v-dialog v-model="showAddChargeDialog" max-width="600">
      <v-card>
        <v-card-title class="headline">Add Charge</v-card-title>
        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12" sm="6">
              <v-select v-model="addChargeForm.chargeType" :items="chargeTypeOptions" item-text="text"
                item-value="value" label="Charge Type *" outlined dense></v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model.number="addChargeForm.amount" label="Amount *" prefix="R" type="number"
                step="0.01" outlined dense @input="generateChargeReference"></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field v-model="addChargeForm.date" label="Due Date *" type="date" outlined dense
                @input="generateChargeReference"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field :value="addChargeForm.reference" label="Reference" outlined dense readonly
                hint="Automatically generated" persistent-hint />
            </v-col>
          </v-row>
          <v-text-field v-model="addChargeForm.description" label="Description (optional – overrides charge type)"
            outlined dense></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showAddChargeDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="submitAddCharge">Add Charge</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ========== ADD CREDIT DIALOG (Statement logic) ========== -->
    <v-dialog v-model="showAddCreditDialog" max-width="600">
      <v-card>
        <v-card-title class="headline">Add Credit / Adjustment</v-card-title>
        <v-card-text class="pt-4">
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field v-model="addCreditForm.description" label="Description *" outlined dense></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field v-model.number="addCreditForm.amount" label="Amount (positive) *" prefix="R" type="number"
                step="0.01" outlined dense @input="generateCreditReference"></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field v-model="addCreditForm.date" label="Date *" type="date" outlined dense
                @input="generateCreditReference"></v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field :value="addCreditForm.reference" label="Reference" outlined dense readonly
                hint="Auto-generated" persistent-hint />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showAddCreditDialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="submitAddCredit">Add Credit</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ========== EDIT DIALOG ========== -->
    <v-dialog v-model="showEditDialog" max-width="500">
      <v-card>
        <v-card-title>Edit Row</v-card-title>
        <v-card-text>
          <v-form ref="editForm" @submit.prevent="submitEdit">
            <v-text-field
              label="Date"
              v-model="editForm.date"
              type="date"
              required
              :rules="[v => !!v || 'Date is required']"
            ></v-text-field>
            <v-text-field
              label="Description"
              v-model="editForm.description"
              required
              :rules="[v => !!v || 'Description is required']"
            ></v-text-field>
            <v-text-field label="Reference" v-model="editForm.reference"></v-text-field>
            <v-text-field
              label="Amount"
              v-model="editForm.amount"
              type="number"
              step="0.01"
              required
              :rules="[v => !!v && parseFloat(v) > 0 || 'Amount must be greater than 0']"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" type="submit" @click="submitEdit">Submit</v-btn>
          <v-btn text @click="showEditDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { collection, addDoc, getDocs, query, where, orderBy, deleteDoc, doc, updateDoc, getDoc, setDoc, writeBatch } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import { logEvent } from "@/utils/logEvent";

pdfMake.vfs = pdfFonts.pdfMake.vfs;
const db = getFirestore();

export default {
  props: {
    leaseReference: { type: String, required: true }
  },

  data() {
    return {
      loading: true,
      showAddPaymentDialog: false,
      showAddChargeDialog: false,
      showAddCreditDialog: false,
      showEditDialog: false,
      showAddChargeInAllocation: false,
      paymentDateMenu: false,
      base64log: "",
      previousBalance: 0,
      savingPayment: false,
      editingPaymentId: null,
      existingAllocations: [],

      // Payment allocation state (statement logic)
      paymentAlloc: { amount: null, date: "", reference: "", allocations: [] },
      newChargeInAllocation: {
        chargeType: "Rent",
        amount: null,
        date: new Date().toISOString().substr(0, 10),
        description: ""
      },

      // Add charge form (statement logic)
      addChargeForm: {
        chargeType: "Rent",
        amount: null,
        date: new Date().toISOString().substr(0, 10),
        description: "",
        reference: ""
      },

      // Add credit form (statement logic)
      addCreditForm: {
        description: "",
        amount: null,
        date: new Date().toISOString().substr(0, 10),
        reference: ""
      },

      // Edit form (invoice logic)
      editForm: {
        id: "",
        type: "",
        date: "",
        description: "",
        reference: "",
        amount: "",
      },

      chargeTypeOptions: [
        { text: "Rent", value: "Rent" },
        { text: "Water", value: "Water" },
        { text: "Sewerage", value: "Sewerage" },
        { text: "Refuse", value: "Refuse" },
        { text: "Admin Fee", value: "Admin Fee" },
        { text: "Parking", value: "Parking" },
        { text: "Bank Fees", value: "Bank Fees" },
        { text: "Lease Fees", value: "Lease Fees" },
        { text: "Bookkeeping", value: "Bookkeeping" },
        { text: "Collection Fee", value: "Collection Fee" },
        { text: "Insurance", value: "Insurance" },
        { text: "Landscaping", value: "Landscaping" },
        { text: "Legal Expenses", value: "Legal Expenses" },
        { text: "Repairs and Maintenance", value: "Repairs and Maintenance" },
        { text: "Utilities", value: "Utilities" },
        { text: "Other Vendor Expenses", value: "Other Vendor Expenses" },
        { text: "Other Charge", value: "Other Charge" }
      ],

      base64Img: "",
      rentAmount: 0,
      depositHeld: 0,
      propertyId: "",
      propertyAddress: "",
      tenantname: "",
      tenantEmail: "",
      tenantId: "",
      totalDue: 0,
      transactions: [],
      tenantAddress: {},
      selectedLandlord: "",
      bankDetails: {},
      currentDate: new Date().toISOString().split('T')[0],
      dueDate: new Date(Date.UTC(new Date().getFullYear(), new Date().getMonth() + 1, 1)).toISOString().split('T')[0],
      invoiceNumber: "",
    };
  },

  computed: {
    invoiceMonth() {
      const now = new Date();
      const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 2));
      const end = new Date(Date.UTC(now.getUTCFullYear() + 10, 11, 31));
      return { start, end };
    },

    sortedEntries() {
      const entries = [];
      const startStr = this.invoiceMonth.start.toISOString().split('T')[0];
      const endStr = this.invoiceMonth.end.toISOString().split('T')[0];

      let runningBalance = 0;
      this.transactions.forEach(t => {
        if (t.datePaid < startStr) {
          const amt = parseFloat(t.amountPaid) || 0;
          runningBalance += amt;
        }
      });

      if (Math.abs(runningBalance) > 0.01) {
        entries.push({
          date: "",
          description: "Balance Brought Forward",
          reference: this.leaseReference,
          amount: runningBalance,
          amountFormatted: this.formatAmount(runningBalance),
          balance: runningBalance,
          balanceFormatted: this.formatCurrency(runningBalance),
          type: "balance",
        });
      }

      const periodTransactions = [];
      this.transactions.forEach(t => {
        if (t.type === "payment_allocation" || t.type === "balance") return;
        if (t.datePaid >= startStr && t.datePaid <= endStr) {
          periodTransactions.push({
            id: t.id,
            date: t.datePaid,
            description: t.description || t.transactionType || "Rent Charged",
            reference: t.reference,
            amount: parseFloat(t.amountPaid),
            type: t.type || "transaction",
            transactionType: t.transactionType,
            rawDate: t.datePaid,
          });
        }
      });

      periodTransactions.sort((a, b) => new Date(a.rawDate || a.date) - new Date(b.rawDate || b.date));

      periodTransactions.forEach(t => {
        runningBalance += t.amount;
        entries.push({
          ...t,
          amountFormatted: this.formatAmount(t.amount),
          balance: runningBalance,
          balanceFormatted: this.formatCurrency(runningBalance),
        });
      });

      return entries;
    },

    agingBuckets() {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      const buckets = { current: 0, days30: 0, days60: 0, days90: 0, days90plus: 0 };

      this.transactions.forEach(t => {
        const amount = parseFloat(t.amountPaid) || 0;

        if (amount <= 0) return;
        if (t.type === "payment" || t.type === "credit" || t.type === "payment_allocation") return;

        const allocated =
          parseFloat(t.allocatedAmount) ||
          parseFloat(t.paidAmount) ||
          (Array.isArray(t.allocations)
            ? t.allocations.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0)
            : 0);
        const remaining = amount - allocated;
        if (remaining <= 0.01) return;

        if (!t.datePaid) return;
        const cd = new Date(t.datePaid + "T00:00:00");
        if (isNaN(cd.getTime())) return;

        let boundary;
        if (today.getDate() === 1) {
          boundary = new Date(today.getFullYear(), today.getMonth() - 1, 2);
        } else {
          boundary = new Date(today.getFullYear(), today.getMonth(), 2);
        }

        if (cd >= boundary) {
          buckets.current += remaining;
          return;
        }

        const boundaries = [boundary];
        for (let i = 1; i <= 4; i++) {
          boundaries.push(new Date(today.getFullYear(), today.getMonth() - i, 2));
        }

        if (cd >= boundaries[1]) {
          buckets.days30 += remaining;
        } else if (cd >= boundaries[2]) {
          buckets.days60 += remaining;
        } else if (cd >= boundaries[3]) {
          buckets.days90 += remaining;
        } else {
          buckets.days90plus += remaining;
        }
      });

      Object.keys(buckets).forEach(k => {
        buckets[k] = Number(buckets[k].toFixed(2));
      });

      return buckets;
    },

    currentBalance() {
      let balance = 0;
      this.transactions
        .filter(t => t.datePaid <= new Date().toISOString().split('T')[0])
        .forEach(t => balance += Number(t.amountPaid || 0));
      return Number(balance.toFixed(2));
    },

    balanceBeforePayment() {
      if (!this.paymentAlloc.date) return this.currentBalance;
      const targetDateStr = this.paymentAlloc.date;

      const relevant = this.transactions
        .filter(t => {
          return (t.datePaid < targetDateStr || (t.datePaid === targetDateStr && t.id !== this.editingPaymentId));
        })
        .sort((a, b) => {
          const dateCompare = a.datePaid.localeCompare(b.datePaid);
          if (dateCompare !== 0) return dateCompare;
          return (b.amountPaid || 0) - (a.amountPaid || 0);
        });

      let balance = 0;
      relevant.forEach(t => balance += Number(t.amountPaid || 0));
      return Number(balance.toFixed(2));
    },

    totalAllocated() {
      return Number(this.paymentAlloc.allocations.reduce((s, a) => s + Number(a.amount || 0), 0).toFixed(2));
    },

    unallocatedAmount() {
      const diff = (this.paymentAlloc.amount || 0) - this.totalAllocated;
      return Number(diff.toFixed(2));
    },

    finalBalanceAfterPayment() {
      return Number((this.balanceBeforePayment - (this.paymentAlloc.amount || 0) + this.totalAllocated).toFixed(2));
    },

    isEditingExistingPaymentWithAllocations() {
      return this.editingPaymentId && this.existingAllocations.length > 0;
    },

    isFuturePaymentDate() {
      if (!this.paymentAlloc.date) return false;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const payDate = new Date(this.paymentAlloc.date);
      return payDate > today;
    }
  },

  methods: {
    formatDate(dateStr) {
      if (!dateStr) return "";
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day} ${month} ${year}`;
    },

    formatCurrency(amount) {
      return `R\u00A0${(parseFloat(amount) || 0).toFixed(2)}`;
    },

    formatAmount(amount) {
      const absAmount = Math.abs(amount);
      if (amount < 0) {
        return `-R\u00A0${absAmount.toFixed(2)}`;
      }
      return `R\u00A0${absAmount.toFixed(2)}`;
    },

    // ====================== PAYMENT REFERENCE GENERATORS ======================
    generatePaymentReference() {
      if (!this.paymentAlloc.date || this.paymentAlloc.amount == null || this.paymentAlloc.amount <= 0) {
        this.paymentAlloc.reference = "";
        return;
      }
      const cleanDate = this.paymentAlloc.date.replace(/-/g, '');
      const cleanAmount = Number(this.paymentAlloc.amount).toFixed(2);
      const ts = Math.floor(Date.now() / 1000);
      this.paymentAlloc.reference = `${this.leaseReference}.${cleanDate}.${cleanAmount}.${ts}`;
    },

    onPaymentAmountChange() {
      this.generatePaymentReference();
      this.clampTotalAllocation();
    },

    onPaymentDateChange() {
      this.generatePaymentReference();
    },

    generateChargeReference() {
      if (!this.addChargeForm.date || this.addChargeForm.amount == null || this.addChargeForm.amount <= 0 || !this.addChargeForm.chargeType) {
        this.addChargeForm.reference = "";
        return;
      }
      const cleanDate = this.addChargeForm.date.replace(/-/g, '');
      const cleanAmount = Number(this.addChargeForm.amount).toFixed(2);
      const shortType = this.addChargeForm.chargeType.substring(0, 4).toUpperCase();
      const ts = Math.floor(Date.now() / 1000);
      this.addChargeForm.reference = `${this.leaseReference}.${shortType}.${cleanDate}.${cleanAmount}.${ts}`;
    },

    generateCreditReference() {
      if (!this.addCreditForm.date || !this.addCreditForm.amount || this.addCreditForm.amount <= 0) {
        this.addCreditForm.reference = "";
        return;
      }
      const cleanDate = this.addCreditForm.date.replace(/-/g, '');
      const cleanAmount = Number(this.addCreditForm.amount).toFixed(2);
      const ts = Math.floor(Date.now() / 1000);
      this.addCreditForm.reference = `${this.leaseReference}.CREDIT.${cleanDate}.${cleanAmount}.${ts}`;
    },

    // ====================== ALLOCATION EDITING HELPERS ======================
    onAllocationInput(i) {
      this.clampAllocation(i);
      this.clampTotalAllocation();
    },

    clampAllocation(i) {
      const a = this.paymentAlloc.allocations[i];
      const max = a.remaining + (a.previouslyAllocated || 0);
      if (a.amount > max) a.amount = Number(max.toFixed(2));
      if (a.amount < 0) a.amount = 0;
      a.amount = Number(a.amount.toFixed(2));
    },

    clampTotalAllocation() {
      const max = this.paymentAlloc.amount || 0;
      let total = this.totalAllocated;
      if (total <= max + 0.01) return;
      const sorted = [...this.paymentAlloc.allocations].sort((a, b) => b.date.localeCompare(a.date));
      for (const alloc of sorted) {
        if (total <= max + 0.01) break;
        const reduceBy = Math.min(alloc.amount, total - max);
        alloc.amount = Number((alloc.amount - reduceBy).toFixed(2));
        total -= reduceBy;
      }
    },

    smartAllocate() {
      const amount = Number(this.paymentAlloc.amount || 0);
      if (amount <= 0) return;
      this.paymentAlloc.allocations.forEach(a => a.amount = a.previouslyAllocated || 0);
      let remaining = amount;
      const sorted = [...this.paymentAlloc.allocations]
        .filter(a => a.remaining > 0.01)
        .sort((a, b) => a.date.localeCompare(b.date));
      for (const alloc of sorted) {
        if (remaining <= 0.01) break;
        const take = Math.min(remaining, alloc.remaining);
        const target = this.paymentAlloc.allocations.find(x => x.chargeId === alloc.chargeId);
        if (target) {
          target.amount = Number((target.amount + take).toFixed(2));
          remaining -= take;
        }
      }
    },

    removeAllocation(i) {
      this.paymentAlloc.allocations.splice(i, 1);
    },

    // ====================== OPEN PAYMENT DIALOG ======================
    async openPaymentAllocation(paymentToEdit = null) {
      this.editingPaymentId = paymentToEdit ? paymentToEdit.id : null;
      const date = paymentToEdit ? paymentToEdit.datePaid : new Date().toISOString().substr(0, 10);
      const paymentAmount = paymentToEdit ? Math.abs(paymentToEdit.amountPaid) : null;
      const paymentReference = paymentToEdit?.reference || "";

      const todayStr = new Date().toISOString().split('T')[0];

      const charges = this.transactions
        .filter(t => Number(t.amountPaid) > 0)
        .map(t => ({
          chargeId: t.id,
          date: t.datePaid,
          description: t.description || t.chargeType || "Charge",
          totalAmount: t.amountPaid,
          allocatedAmount: t.allocatedAmount || 0,
          allocations: t.allocations || [],
          remaining: Number((t.amountPaid - (t.allocatedAmount || 0)).toFixed(2))
        }));

      const existingFromThisPayment = charges
        .flatMap(c => c.allocations
          .filter(alloc => alloc.paymentRef === paymentReference)
          .map(alloc => ({
            chargeId: c.chargeId,
            date: c.date,
            description: c.description,
            remaining: c.remaining,
            previouslyAllocated: alloc.amount,
            amount: alloc.amount
          }))
        );

      this.existingAllocations = [...existingFromThisPayment];

      let filteredCharges;
      if (this.editingPaymentId) {
        filteredCharges = charges.filter(c =>
          c.remaining > 0.01 ||
          c.allocations.some(alloc => alloc.paymentRef === paymentReference)
        );
      } else {
        filteredCharges = charges.filter(c => c.date <= todayStr);
      }

      const allocRows = filteredCharges.map(c => ({
        chargeId: c.chargeId,
        date: c.date,
        description: c.description,
        remaining: c.remaining,
        previouslyAllocated: c.allocations.find(alloc => alloc.paymentRef === paymentReference)?.amount || 0,
        amount: c.allocations.find(alloc => alloc.paymentRef === paymentReference)?.amount || 0
      }));

      allocRows.sort((a, b) => a.date.localeCompare(b.date));

      let autoRef = paymentReference;
      if (!this.editingPaymentId) {
        if (paymentAmount && date) {
          const cleanDate = date.replace(/-/g, '');
          const cleanAmount = Number(paymentAmount).toFixed(2);
          const ts = Math.floor(Date.now() / 1000);
          autoRef = `${this.leaseReference}.${cleanDate}.${cleanAmount}.${ts}`;
        } else {
          autoRef = "";
        }
      }

      this.paymentAlloc = {
        amount: paymentAmount,
        date: date,
        reference: autoRef,
        allocations: allocRows
      };

      this.showAddPaymentDialog = true;
    },

    cancelPaymentDialog() {
      this.showAddPaymentDialog = false;
      this.editingPaymentId = null;
      this.existingAllocations = [];
      this.paymentAlloc = { amount: null, date: "", reference: "", allocations: [] };
    },

    // ====================== SAVE PAYMENT ======================
    async savePaymentAllocation() {
      if (this.unallocatedAmount < -0.01) {
        alert("Over-allocated! Adjust amounts.");
        return;
      }
      this.savingPayment = true;
      const total = Number((this.paymentAlloc.amount || 0).toFixed(2));
      if (total <= 0 || !this.paymentAlloc.date) {
        alert("Invalid amount or date");
        this.savingPayment = false;
        return;
      }

      let paymentReference = this.paymentAlloc.reference;
      const looksUnique =
        typeof paymentReference === 'string' &&
        paymentReference.length > this.leaseReference.length + 4 &&
        paymentReference !== this.leaseReference &&
        paymentReference.includes('.');

      if (!looksUnique) {
        const cleanDate = this.paymentAlloc.date.replace(/-/g, '');
        const cleanAmount = Number(total).toFixed(2);
        const ts = Math.floor(Date.now() / 1000);
        paymentReference = `${this.leaseReference}.${cleanDate}.${cleanAmount}.${ts}`;
        this.paymentAlloc.reference = paymentReference;
      }

      const batch = writeBatch(db);
      let payRef;
      if (this.editingPaymentId) {
        payRef = doc(db, "Transactions", this.editingPaymentId);
        batch.update(payRef, {
          amountPaid: -total,
          datePaid: this.paymentAlloc.date,
          reference: paymentReference,
          type: "payment",
          leaseReference: this.leaseReference
        });
      } else {
        payRef = doc(collection(db, "Transactions"));
        batch.set(payRef, {
          leaseReference: this.leaseReference,
          datePaid: this.paymentAlloc.date,
          description: "Payment Received",
          reference: paymentReference,
          amountPaid: -total,
          type: "payment",
          allocatedAmount: 0,
          allocations: [],
          createdAt: new Date().toISOString()
        });
      }

      const newAllocMap = new Map();
      this.paymentAlloc.allocations.forEach(a => {
        const amt = Number(a.amount) || 0;
        if (a.chargeId && amt > 0.01) {
          newAllocMap.set(a.chargeId, Number(amt.toFixed(2)));
        }
      });

      for (const [chargeId, newAmount] of newAllocMap) {
        const chargeRef = doc(db, "Transactions", chargeId);
        const chargeSnap = await getDoc(chargeRef);
        if (!chargeSnap.exists()) continue;

        const chargeData = chargeSnap.data();
        let allocations = Array.isArray(chargeData.allocations) ? chargeData.allocations.slice() : [];

        const existingIndex = allocations.findIndex(alloc =>
          alloc.paymentRef === paymentReference ||
          alloc.paymentRef === this.paymentAlloc.reference
        );
        const oldAmount = existingIndex !== -1 ? Number(allocations[existingIndex].amount) || 0 : 0;

        if (newAmount > 0.01) {
          const allocEntry = {
            paymentRef: paymentReference,
            amount: Number(newAmount.toFixed(2)),
            date: this.paymentAlloc.date,
            paymentMethod: null,
            paymentId: payRef.id
          };
          if (existingIndex !== -1) {
            allocations[existingIndex] = allocEntry;
          } else {
            allocations.push(allocEntry);
          }
        } else if (oldAmount > 0.01) {
          allocations = allocations.filter(alloc => alloc.paymentRef !== paymentReference);
        }

        const newAllocatedAmount = Number(
          allocations.reduce((sum, alloc) => sum + (Number(alloc.amount) || 0), 0).toFixed(2)
        );

        batch.update(chargeRef, {
          allocations: allocations,
          allocatedAmount: newAllocatedAmount,
          type: 'charge',
          chargeType: chargeData.chargeType || chargeData.transactionType || 'Other Charge'
        });
      }

      for (const old of this.existingAllocations) {
        const newAmount = newAllocMap.get(old.chargeId) || 0;
        if (newAmount === 0 && old.previouslyAllocated > 0.01) {
          const chargeRef = doc(db, "Transactions", old.chargeId);
          const chargeSnap = await getDoc(chargeRef);
          if (chargeSnap.exists()) {
            const chargeData = chargeSnap.data();
            let allocations = Array.isArray(chargeData.allocations) ? chargeData.allocations : [];
            allocations = allocations.filter(
              alloc => alloc.paymentRef !== paymentReference &&
                       alloc.paymentRef !== this.paymentAlloc.reference
            );
            const newAllocatedAmount = Number(
              allocations.reduce((sum, alloc) => sum + (Number(alloc.amount) || 0), 0).toFixed(2)
            );
            batch.update(chargeRef, {
              allocations: allocations,
              allocatedAmount: newAllocatedAmount,
              type: 'charge'
            });
          }
        }
      }

      await batch.commit();

      await logEvent(
        this.editingPaymentId ? "Payment Edited" : "Payment Saved",
        `R ${total} → ${this.leaseReference}`,
        "Lease Invoice"
      );

      alert(this.editingPaymentId ? "Payment updated successfully" : "Payment saved");
      this.cancelPaymentDialog();
      this.savingPayment = false;
      await this.fetchTransactions();
    },

    // ====================== ADD CHARGE ======================
    async submitAddCharge() {
      if (!this.addChargeForm.chargeType || !this.addChargeForm.amount || this.addChargeForm.amount <= 0 || !this.addChargeForm.date) {
        alert("Please fill in Charge Type, Amount, and Date");
        return;
      }
      const amount = Number(this.addChargeForm.amount);
      const chargeRef = doc(collection(db, "Transactions"));
      await setDoc(chargeRef, {
        leaseReference: this.leaseReference,
        datePaid: this.addChargeForm.date,
        description: this.addChargeForm.description || this.addChargeForm.chargeType,
        reference: this.addChargeForm.reference,
        amountPaid: amount,
        type: "charge",
        allocatedAmount: 0,
        allocations: [],
        chargeType: this.addChargeForm.chargeType,
        createdAt: new Date().toISOString()
      });

      await logEvent(
        "Charge Added",
        `${this.addChargeForm.description || this.addChargeForm.chargeType} – R ${amount.toFixed(2)}`,
        "Lease Invoice"
      );

      this.addChargeForm = {
        chargeType: "Rent",
        amount: null,
        date: new Date().toISOString().substr(0, 10),
        description: "",
        reference: ""
      };
      this.showAddChargeDialog = false;
      await this.fetchTransactions();
      alert("Charge added successfully");
    },

    // ====================== ADD CREDIT ======================
    openAddCreditDialog() {
      this.addCreditForm = {
        description: "",
        amount: null,
        date: new Date().toISOString().substr(0, 10),
        reference: ""
      };
      this.generateCreditReference();
      this.showAddCreditDialog = true;
    },

    async submitAddCredit() {
      if (!this.addCreditForm.description || !this.addCreditForm.amount || this.addCreditForm.amount <= 0 || !this.addCreditForm.date) {
        alert("Please fill in Description, Amount, and Date");
        return;
      }
      const amount = Number(this.addCreditForm.amount);
      const creditRef = doc(collection(db, "Transactions"));
      await setDoc(creditRef, {
        leaseReference: this.leaseReference,
        datePaid: this.addCreditForm.date,
        description: this.addCreditForm.description,
        reference: this.addCreditForm.reference,
        amountPaid: -amount,
        type: "credit",
        createdAt: new Date().toISOString()
      });

      await logEvent(
        "Credit Issued",
        `${this.addCreditForm.description} – R ${amount.toFixed(2)}`,
        "Lease Invoice"
      );

      this.showAddCreditDialog = false;
      await this.fetchTransactions();
      alert("Credit added successfully");
    },

    // ====================== ADD CHARGE IN ALLOCATION ======================
    async addChargeInAllocation() {
      if (!this.newChargeInAllocation.chargeType || !this.newChargeInAllocation.amount || this.newChargeInAllocation.amount <= 0 || !this.newChargeInAllocation.date) {
        alert("Please fill in Charge Type, Amount, and Date");
        return;
      }
      const amount = Number(this.newChargeInAllocation.amount);
      const chargeRef = doc(collection(db, "Transactions"));
      await setDoc(chargeRef, {
        leaseReference: this.leaseReference,
        datePaid: this.newChargeInAllocation.date,
        description: this.newChargeInAllocation.description || this.newChargeInAllocation.chargeType,
        reference: "MANUAL CHARGE (from payment)",
        amountPaid: amount,
        type: "charge",
        allocatedAmount: 0,
        allocations: [],
        chargeType: this.newChargeInAllocation.chargeType,
        createdAt: new Date().toISOString()
      });
      this.paymentAlloc.allocations.push({
        chargeId: chargeRef.id,
        date: this.newChargeInAllocation.date,
        description: this.newChargeInAllocation.description || this.newChargeInAllocation.chargeType,
        remaining: amount,
        amount: 0,
        previouslyAllocated: 0
      });
      this.newChargeInAllocation = {
        chargeType: "Rent",
        amount: null,
        date: this.paymentAlloc.date || new Date().toISOString().substr(0, 10),
        description: ""
      };
      this.showAddChargeInAllocation = false;
      await this.fetchTransactions();
    },

    // ====================== FETCH DATA ======================
    async fetchInvoiceDetails() {
      try {
        const leaseQuery = query(
          collection(db, "Leases"),
          where("leaseReference", "==", this.leaseReference)
        );
        const leaseSnapshot = await getDocs(leaseQuery);

        if (!leaseSnapshot.empty) {
          const leaseData = leaseSnapshot.docs[0].data();
          this.propertyId = leaseData.propertyId;
          this.tenantId = leaseData.tenantId;
          this.rentAmount = `${(parseFloat(leaseData.leaseDetails.rentalAmount) || 0).toFixed(2)}`;
          this.depositHeld = (parseFloat(leaseData.depositHeld) || 0).toFixed(2);

          const propertyQuery = query(
            collection(db, "properties"),
            where("id", "==", this.propertyId)
          );
          const propertySnapshot = await getDocs(propertyQuery);

          if (!propertySnapshot.empty) {
            const propertyData = propertySnapshot.docs[0].data();
            this.tenantAddress = propertyData.address;
            this.propertyAddress = `${propertyData.address.building || ""}, ${
              propertyData.address.street
            }, ${propertyData.address.suburb}, ${propertyData.address.city}, ${
              propertyData.address.postalCode
            }`;
            this.selectedLandlord = propertyData.selectedLandlord || "Not Available";

            const landlordQuery = query(
              collection(db, "landlords"),
              where("reference", "==", this.selectedLandlord)
            );
            const landlordSnapshot = await getDocs(landlordQuery);

            if (!landlordSnapshot.empty) {
              const landlordData = landlordSnapshot.docs[0].data();
              this.bankDetails = landlordData.bankAccount || {};
            }
          }

          const tenantQuery = query(
            collection(db, "tenants"),
            where("reference", "==", this.tenantId)
          );
          const tenantSnapshot = await getDocs(tenantQuery);

          if (!tenantSnapshot.empty) {
            const tenantData = tenantSnapshot.docs[0].data();
            this.tenantname = tenantData.displayAs || "N/A";
            this.tenantEmail = tenantData.email || "N/A";
          }
        } else {
          console.error("Lease not found");
        }
      } catch (error) {
        console.error("Error fetching invoice details:", error);
      }
    },

    async fetchTransactions() {
      try {
        const transactionQuery = query(
          collection(db, "Transactions"),
          where("leaseReference", "==", this.leaseReference),
          orderBy("datePaid")
        );
        const transactionSnapshot = await getDocs(transactionQuery);

        const startStr = this.invoiceMonth.start.toISOString().split('T')[0];
        const endStr = this.invoiceMonth.end.toISOString().split('T')[0];

        let totalTransactionAmount = 0;
        const transactions = [];

        transactionSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          const datePaid = data.datePaid;

          transactions.push({
            id: doc.id,
            datePaid: data.datePaid,
            description: data.description || data.transactionType || "N/A",
            reference: data.reference || "N/A",
            amountPaid: parseFloat(data.amountPaid) || 0,
            type: data.type || "transaction",
            transactionType: data.transactionType,
            chargeType: data.chargeType,
            allocations: data.allocations || [],
            allocatedAmount: parseFloat(data.allocatedAmount) || 0,
            paidAmount: parseFloat(data.paidAmount) || 0,
          });

          if (datePaid >= startStr && datePaid <= endStr) {
            totalTransactionAmount += parseFloat(data.amountPaid) || 0;
          }
        });

        this.transactions = transactions;

        let previousBalance = 0;
        transactions.forEach(t => {
          if (t.datePaid < startStr) previousBalance += t.amountPaid;
        });

        const totalDueAmount = previousBalance + totalTransactionAmount;
        this.totalDue = this.formatCurrency(totalDueAmount);
        this.previousBalance = previousBalance;
      } catch (error) {
        console.error("Error fetching transactions:", error);
      }
    },

    async generateInvoiceNumber() {
      try {
        const currentDate = new Date();
        const formattedDate = currentDate.toISOString().split("T")[0].replace(/-/g, "");
        const randomDigits = Math.floor(Math.random() * 900) + 100;
        this.invoiceNumber = `INV${formattedDate}${randomDigits}`;
      } catch (error) {
        console.error("Error generating invoice number:", error);
      }
    },

    async fetchLogoBase64() {
      try {
        const logosRef = collection(db, "Logo");
        const q = query(logosRef, where("id", "==", 2));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const logoDoc = querySnapshot.docs[0];
          const logoData = logoDoc.data();
          this.base64log = logoData.logo2;
        } else {
          console.error("Logo not found");
          this.base64log = "";
        }
      } catch (error) {
        console.error("Error fetching logo:", error);
        this.base64log = "";
      }
    },

    // ====================== EDIT / DELETE ROW ======================
    editRow(row) {
      if (!row.id) {
        alert("Cannot edit: missing document ID");
        return;
      }

      const tx = this.transactions.find(t => t.id === row.id);

      // Payments → open the allocation editor (statement logic)
      if (tx && (tx.type === 'payment' || (tx.amountPaid < 0 && tx.type === 'payment'))) {
        this.openPaymentAllocation(tx);
        return;
      }

      // Charges & credits → simple edit dialog
      this.editForm = {
        id: row.id,
        type: row.type,
        date: row.rawDate || row.date,
        description: row.description,
        reference: row.reference,
        amount: Math.abs(parseFloat(row.amount)).toFixed(2),
      };
      this.showEditDialog = true;
    },

    async submitEdit() {
      if (!this.$refs.editForm.validate()) return;
      try {
        if (!this.editForm.id) throw new Error("No ID provided");

        let amountToStore = parseFloat(this.editForm.amount);
        if (this.editForm.type === "credit" || this.editForm.type === "payment") {
          amountToStore = -Math.abs(amountToStore);
        } else {
          amountToStore = Math.abs(amountToStore);
        }

        const docRef = doc(db, "Transactions", this.editForm.id);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
          alert("Transaction no longer exists");
          this.showEditDialog = false;
          return;
        }

        const updateData = {
          datePaid: this.editForm.date,
          description: this.editForm.description,
          reference: this.editForm.reference,
          amountPaid: amountToStore,
        };

        await updateDoc(docRef, updateData);

        await logEvent(
          "Transaction Edited",
          `${this.editForm.description} – R ${Math.abs(amountToStore).toFixed(2)}`,
          "Lease Invoice"
        );

        this.showEditDialog = false;
        await this.fetchTransactions();
        alert("Row updated successfully!");
      } catch (error) {
        console.error("Error updating row:", error);
        alert("Failed to update row: " + error.message);
      }
    },

    async deleteRow(row) {
      if (!row.id) {
        alert("Cannot delete: missing document ID");
        return;
      }
      if (!confirm("Are you sure you want to delete this row?")) return;

      try {
        const docRef = doc(db, "Transactions", row.id);
        const docSnap = await getDoc(docRef);
        if (!docSnap.exists()) {
          alert("Transaction not found");
          return;
        }
        await deleteDoc(docRef);

        await logEvent(
          "Transaction Deleted",
          `${row.description} – R ${Math.abs(parseFloat(row.amount)).toFixed(2)}`,
          "Lease Invoice"
        );

        await this.fetchTransactions();
        alert("Row deleted successfully!");
      } catch (error) {
        console.error("Delete error:", error);
        alert("Failed to delete: " + error.message);
      }
    },

    // ====================== PDF GENERATION (unchanged) ======================
    async generatePDF() {
      const fonts = {
        Roboto: {
          normal: "assets/Roboto/static/Roboto-Regular.ttf",
          bold: "assets/Roboto/static/Roboto-Bold.ttf",
          italics: "assets/Roboto/static/Roboto-Italic.ttf",
          bolditalics: "assets/Roboto/static/Roboto-BoldItalic.ttf",
        },
      };

      pdfMake.fonts = fonts;

      if (!this.tenantAddress) this.tenantAddress = {};
      if (!this.bankDetails) this.bankDetails = {};

      const docDefinition = {
        defaultStyle: { font: "Roboto" },
        content: [
          {
            stack: [
              {
                canvas: [{ type: "rect", x: 0, y: 0, w: 420, h: 180, color: "black" }],
                margin: [10, 10, 10, 10],
                alignment: "center",
                image: this.base64log || "data:image/png;base64,",
                width: 400,
                margin: [0, 20, 0, 20],
              },
            ],
            alignment: "center",
          },
          {
            columns: [
              {
                width: "50%",
                stack: [
                  { text: "Invoice to", style: "invoiceToHeader", margin: [0, 0, 0, 5] },
                  { text: `${this.tenantname?.toUpperCase() || "N/A"}`, style: "invoiceto" },
                  { text: `${this.tenantAddress.street?.toUpperCase() || "N/A"}`, style: "invoiceTo" },
                  { text: `${this.tenantAddress.city?.toUpperCase() || "N/A"}`, style: "invoiceTo" },
                  { text: `${this.tenantAddress.postalCode?.toUpperCase() || "N/A"}`, style: "invoiceTo" },
                ],
              },
              {
                width: "50%",
                stack: [
                  { text: "TAX INVOICE", style: "subheader", alignment: "right", margin: [0, 0, 0, 20] },
                  { text: `NO. ${this.invoiceNumber || "N/A"}`, style: "invoiceDetails", alignment: "right", bold: true },
                  { text: `INVOICE DATE: ${this.formatDate(this.currentDate) || "N/A"}`, style: "invoiceDetails", alignment: "right" },
                  { text: `PAYMENT REF: ${this.leaseReference || "N/A"}`, style: "invoiceDetails", alignment: "right" },
                ],
              },
            ],
            margin: [0, 0, 0, 20],
          },
          { text: `Property: ${this.propertyAddress || "N/A"}`, style: "propertyAddress", alignment: "center", margin: [0, 0, 0, 10] },
          {
            table: {
              widths: ["*", "*", "*"],
              body: [[
                { text: `DUE DATE: ${this.formatDate(this.dueDate) || "N/A"}`, style: "headerLabelHighlighted", alignment: "center", padding: [5, 0, 5, 0], border: [true, true, true, true] },
                { text: `PAYMENT REF: ${this.leaseReference || "N/A"}`, style: "headerLabelHighlighted", alignment: "center", padding: [5, 0, 5, 0], border: [true, true, true, true] },
                { text: `DEPOSIT HELD: R${this.depositHeld || "0.00"}`, style: "headerLabelHighlighted", alignment: "center", padding: [5, 0, 5, 0], border: [true, true, true, true] },
              ]],
            },
            layout: {
              hLineColor: () => "#FFFFFF",
              vLineColor: () => "#FFFFFF",
              paddingLeft: () => 5,
              paddingRight: () => 5,
              paddingTop: () => 5,
              paddingBottom: () => 5,
            },
            margin: [0, 10, 0, 10],
          },
          {
            table: {
              headerRows: 1,
              widths: ["15%", "35%", "10%", "20%", "20%"],
              body: [
                [
                  { text: "DATE", style: "tableHeader" },
                  { text: "DESCRIPTION", style: "tableHeader" },
                  { text: "VAT", style: "tableHeader" },
                  { text: "AMOUNT Excl", style: "tableHeader" },
                  { text: "BALANCE", style: "tableHeader" },
                ],
                ...this.sortedEntries.map((entry) => [
                  { text: entry.date || "", style: "tableData" },
                  { text: entry.description || "N/A", style: "tableData" },
                  { text: "0.00", style: "tableData" },
                  { text: entry.amountFormatted || this.formatAmount(entry.amount), style: "tableData" },
                  { text: entry.balanceFormatted || this.formatCurrency(entry.balance), style: "tableData" },
                ]),
              ],
            },
            layout: "lightHorizontalLines",
            margin: [0, 0, 0, 20],
          },
          {
            table: {
              widths: ["*", "*", "*", "*"],
              body: [
                [
                  { text: "", style: "totalLabel" },
                  { text: "", style: "totalLabel" },
                  { text: "SUB TOTAL", style: "totalLabel", alignment: "left", padding: [5, 0, 5, 0] },
                  { text: `${this.totalDue || "R 0.00"}`, style: "totalValue", alignment: "right", padding: [5, 0, 5, 0] },
                ],
                [
                  { text: "", style: "totalLabel" },
                  { text: "", style: "totalLabel" },
                  { text: "VAT", style: "totalLabel", alignment: "left", padding: [5, 0, 5, 0] },
                  { text: "R 0.00", style: "totalValue", alignment: "right", padding: [5, 0, 5, 0] },
                ],
                [
                  { text: "", style: "totalLabel", padding: [5, 0, 5, 0] },
                  { text: "", style: "totalLabel" },
                  { text: "TOTAL DUE", style: "totalLabelHighlighted", alignment: "left", padding: [5, 0, 5, 0] },
                  { text: `${this.totalDue || "R 0.00"}`, style: "totalValueHighlighted", alignment: "right", padding: [5, 0, 5, 0] },
                ],
              ],
            },
            layout: "noBorders",
            margin: [0, 10, 0, 10],
          },
          { text: "Ageing Analysis", style: "invoiceto", margin: [0, 20, 0, 10] },
          {
            table: {
              widths: ["*", "*", "*", "*", "*"],
              body: [
                [
                  { text: "Current", style: "tableHeader", alignment: "center" },
                  { text: "0 - 30 days", style: "tableHeader", alignment: "center" },
                  { text: "31 - 60 days", style: "tableHeader", alignment: "center" },
                  { text: "61 - 90 days", style: "tableHeader", alignment: "center" },
                  { text: "90+ days", style: "tableHeader", alignment: "center" },
                ],
                [
                  { text: this.formatCurrency(this.agingBuckets.current), style: "tableData", alignment: "center" },
                  { text: this.formatCurrency(this.agingBuckets.days30), style: "tableData", alignment: "center" },
                  { text: this.formatCurrency(this.agingBuckets.days60), style: "tableData", alignment: "center" },
                  { text: this.formatCurrency(this.agingBuckets.days90), style: "tableData", alignment: "center" },
                  { text: this.formatCurrency(this.agingBuckets.days90plus), style: "tableData", alignment: "center" },
                ],
              ],
            },
            layout: "lightHorizontalLines",
            margin: [0, 0, 0, 20],
          },
          { text: "PAYMENT DETAILS", style: "invoiceto", margin: [0, 20, 0, 10] },
          {
            text: `Bank details: ${this.bankDetails.name || "N/A"}, ${this.bankDetails.bank || "N/A"}, ${
              this.bankDetails.branchCode || "N/A"
            }, Account No: ${this.bankDetails.number || "N/A"}\nUse payment reference: ${
              this.leaseReference || "N/A"
            }`,
            style: "paymentDetails",
            margin: [0, 0, 0, 20],
          },
        ],
        footer: (currentPage, pageCount) => ({
          stack: [
            { canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: "#000000" }], margin: [0, 0, 0, 5] },
            { text: "GQ Property Group | 33 Donkin Street Central Port Elizabeth Eastern Cape\n0615175157 | rent@gqproperty.co.za", style: "footer", alignment: "center" },
            { text: `Page ${currentPage} of ${pageCount}`, fontSize: 8, alignment: "right", margin: [0, 5, 10, 0] },
          ],
        }),
        styles: {
          header: { fontSize: 24, bold: true, color: "#000080" },
          subheader: { fontSize: 24, bold: true, color: "#F1C284" },
          invoiceto: { fontSize: 16, bold: true, color: "#F1C284" },
          invoiceToHeader: { fontSize: 12, bold: true },
          invoiceTo: { fontSize: 10 },
          invoiceDetails: { fontSize: 10 },
          propertyAddress: { fontSize: 10, bold: true },
          tableHeader: { bold: true, fontSize: 10, fillColor: "#F1C284", color: "#FFFFFF", alignment: "left" },
          tableData: { fontSize: 10, alignment: "left" },
          totalLabel: { fontSize: 12, bold: true },
          totalValue: { fontSize: 12, bold: true },
          headerLabelHighlighted: { fontSize: 8, bold: true, color: "#FFFFFF", fillColor: "#3c3c3c", margin: [0, 5, 0, 5], padding: [5, 5, 5, 5], border: [false, true, false, true] },
          totalLabelHighlighted: { fontSize: 10, bold: true, color: "#FFFFFF", fillColor: "#3c3c3c", margin: [0, 5, 0, 5], padding: [5, 5, 5, 5], border: [false, true, false, true] },
          totalValueHighlighted: { fontSize: 10, bold: true, color: "#FFFFFF", fillColor: "#F1C284", alignment: "center", margin: [0, 5, 0, 5], padding: [5, 5, 5, 5] },
          paymentDetailsHeader: { fontSize: 10, bold: true },
          paymentDetails: { fontSize: 10 },
          footer: { fontSize: 10 },
        },
      };

      pdfMake.createPdf(docDefinition).open();
    },

    async generatePDF_Data() {
      const fonts = {
        Roboto: {
          normal: "assets/Roboto/static/Roboto-Regular.ttf",
          bold: "assets/Roboto/static/Roboto-Bold.ttf",
          italics: "assets/Roboto/static/Roboto-Italic.ttf",
          bolditalics: "assets/Roboto/static/Roboto-BoldItalic.ttf",
        },
      };

      pdfMake.fonts = fonts;

      if (!this.tenantAddress) this.tenantAddress = {};
      if (!this.bankDetails) this.bankDetails = {};

      return new Promise((resolve, reject) => {
        const docDefinition = {
          defaultStyle: { font: "Roboto" },
          content: [
            {
              stack: [
                {
                  canvas: [{ type: "rect", x: 0, y: 0, w: 420, h: 180, color: "black" }],
                  margin: [10, 10, 10, 10],
                  alignment: "center",
                  image: this.base64log || "data:image/png;base64,",
                  width: 400,
                  margin: [0, 20, 0, 20],
                },
              ],
              alignment: "center",
            },
            {
              columns: [
                {
                  width: "50%",
                  stack: [
                    { text: "Invoice to", style: "invoiceToHeader", margin: [0, 0, 0, 5] },
                    { text: `${this.tenantname?.toUpperCase() || "N/A"}`, style: "invoiceto" },
                    { text: `${this.tenantAddress.street?.toUpperCase() || "N/A"}`, style: "invoiceTo" },
                    { text: `${this.tenantAddress.city?.toUpperCase() || "N/A"}`, style: "invoiceTo" },
                    { text: `${this.tenantAddress.postalCode?.toUpperCase() || "N/A"}`, style: "invoiceTo" },
                  ],
                },
                {
                  width: "50%",
                  stack: [
                    { text: "TAX INVOICE", style: "subheader", alignment: "right", margin: [0, 0, 0, 20] },
                    { text: `NO. ${this.invoiceNumber || "N/A"}`, style: "invoiceDetails", alignment: "right", bold: true },
                    { text: `INVOICE DATE: ${this.formatDate(this.currentDate) || "N/A"}`, style: "invoiceDetails", alignment: "right" },
                    { text: `PAYMENT REF: ${this.leaseReference || "N/A"}`, style: "invoiceDetails", alignment: "right" },
                  ],
                },
              ],
              margin: [0, 0, 0, 20],
            },
            { text: `Property: ${this.propertyAddress || "N/A"}`, style: "propertyAddress", alignment: "center", margin: [0, 0, 0, 10] },
            {
              table: {
                widths: ["*", "*", "*"],
                body: [[
                  { text: `DUE DATE: ${this.formatDate(this.dueDate) || "N/A"}`, style: "headerLabelHighlighted", alignment: "center", padding: [5, 0, 5, 0], border: [true, true, true, true] },
                  { text: `PAYMENT REF: ${this.leaseReference || "N/A"}`, style: "headerLabelHighlighted", alignment: "center", padding: [5, 0, 5, 0], border: [true, true, true, true] },
                  { text: `DEPOSIT HELD: R${this.depositHeld || "0.00"}`, style: "headerLabelHighlighted", alignment: "center", padding: [5, 0, 5, 0], border: [true, true, true, true] },
                ]],
              },
              layout: { hLineColor: () => "#FFFFFF", vLineColor: () => "#FFFFFF" },
              margin: [0, 10, 0, 10],
            },
            {
              table: {
                headerRows: 1,
                widths: ["15%", "35%", "10%", "20%", "20%"],
                body: [
                  [
                    { text: "DATE", style: "tableHeader" },
                    { text: "DESCRIPTION", style: "tableHeader" },
                    { text: "VAT", style: "tableHeader" },
                    { text: "AMOUNT Excl", style: "tableHeader" },
                    { text: "BALANCE", style: "tableHeader" },
                  ],
                  ...this.sortedEntries.map((entry) => [
                    { text: entry.date || "", style: "tableData" },
                    { text: entry.description || "N/A", style: "tableData" },
                    { text: "0.00", style: "tableData" },
                    { text: entry.amountFormatted || this.formatAmount(entry.amount), style: "tableData" },
                    { text: entry.balanceFormatted || this.formatCurrency(entry.balance), style: "tableData" },
                  ]),
                ],
              },
              layout: "lightHorizontalLines",
              margin: [0, 0, 0, 20],
            },
            {
              table: {
                widths: ["*", "*", "*", "*"],
                body: [
                  [
                    { text: "", style: "totalLabel" },
                    { text: "", style: "totalLabel" },
                    { text: "SUB TOTAL", style: "totalLabel", alignment: "left" },
                    { text: `${this.totalDue || "R 0.00"}`, style: "totalValue", alignment: "right" },
                  ],
                  [
                    { text: "", style: "totalLabel" },
                    { text: "", style: "totalLabel" },
                    { text: "VAT", style: "totalLabel", alignment: "left" },
                    { text: "R 0.00", style: "totalValue", alignment: "right" },
                  ],
                  [
                    { text: "", style: "totalLabel" },
                    { text: "", style: "totalLabel" },
                    { text: "TOTAL DUE", style: "totalLabelHighlighted", alignment: "left" },
                    { text: `${this.totalDue || "R 0.00"}`, style: "totalValueHighlighted", alignment: "right" },
                  ],
                ],
              },
              layout: "noBorders",
              margin: [0, 10, 0, 10],
            },
            { text: "Ageing Analysis", style: "invoiceto", margin: [0, 20, 0, 10] },
            {
              table: {
                widths: ["*", "*", "*", "*", "*"],
                body: [
                  [
                    { text: "Current", style: "tableHeader", alignment: "center" },
                    { text: "0 - 30 days", style: "tableHeader", alignment: "center" },
                    { text: "31 - 60 days", style: "tableHeader", alignment: "center" },
                    { text: "61 - 90 days", style: "tableHeader", alignment: "center" },
                    { text: "90+ days", style: "tableHeader", alignment: "center" },
                  ],
                  [
                    { text: this.formatCurrency(this.agingBuckets.current), style: "tableData", alignment: "center" },
                    { text: this.formatCurrency(this.agingBuckets.days30), style: "tableData", alignment: "center" },
                    { text: this.formatCurrency(this.agingBuckets.days60), style: "tableData", alignment: "center" },
                    { text: this.formatCurrency(this.agingBuckets.days90), style: "tableData", alignment: "center" },
                    { text: this.formatCurrency(this.agingBuckets.days90plus), style: "tableData", alignment: "center" },
                  ],
                ],
              },
              layout: "lightHorizontalLines",
              margin: [0, 0, 0, 20],
            },
            { text: "PAYMENT DETAILS", style: "invoiceto", margin: [0, 20, 0, 10] },
            {
              text: `Bank details: ${this.bankDetails.name || "N/A"}, ${this.bankDetails.bank || "N/A"}, ${
                this.bankDetails.branchCode || "N/A"
              }, Account No: ${this.bankDetails.number || "N/A"}\nUse payment reference: ${
                this.leaseReference || "N/A"
              }`,
              style: "paymentDetails",
              margin: [0, 0, 0, 20],
            },
          ],
          footer: (currentPage, pageCount) => ({
            stack: [
              { canvas: [{ type: "line", x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 1, lineColor: "#000000" }], margin: [0, 0, 0, 5] },
              { text: "GQ Property Group | 33 Donkin Street Central Port Elizabeth Eastern Cape\n0615175157 | rent@gqproperty.co.za", style: "footer", alignment: "center" },
              { text: `Page ${currentPage} of ${pageCount}`, fontSize: 8, alignment: "right", margin: [0, 5, 10, 0] },
            ],
          }),
          styles: {
            header: { fontSize: 24, bold: true, color: "#000080" },
            subheader: { fontSize: 24, bold: true, color: "#F1C284" },
            invoiceto: { fontSize: 16, bold: true, color: "#F1C284" },
            invoiceToHeader: { fontSize: 12, bold: true },
            invoiceTo: { fontSize: 10 },
            invoiceDetails: { fontSize: 10 },
            propertyAddress: { fontSize: 10, bold: true },
            tableHeader: { bold: true, fontSize: 10, fillColor: "#F1C284", color: "#FFFFFF", alignment: "left" },
            tableData: { fontSize: 10, alignment: "left" },
            totalLabel: { fontSize: 12, bold: true },
            totalValue: { fontSize: 12, bold: true },
            headerLabelHighlighted: { fontSize: 8, bold: true, color: "#FFFFFF", fillColor: "#3c3c3c", margin: [0, 5, 0, 5], padding: [5, 5, 5, 5], border: [false, true, false, true] },
            totalLabelHighlighted: { fontSize: 10, bold: true, color: "#FFFFFF", fillColor: "#3c3c3c", margin: [0, 5, 0, 5], padding: [5, 5, 5, 5], border: [false, true, false, true] },
            totalValueHighlighted: { fontSize: 10, bold: true, color: "#FFFFFF", fillColor: "#F1C284", alignment: "center", margin: [0, 5, 0, 5], padding: [5, 5, 5, 5] },
            paymentDetailsHeader: { fontSize: 10, bold: true },
            paymentDetails: { fontSize: 10 },
            footer: { fontSize: 10 },
          },
        };

        pdfMake.createPdf(docDefinition).getBase64((data) => {
          resolve(data);
        }, (error) => {
          reject(error);
        });
      });
    },

    async sendInvoiceByEmail() {
      this.loading = true;
      try {
        const pdfBase64 = await this.generatePDF_Data();
        const currentDate = new Date();
        const formattedDate = currentDate.toISOString().split("T")[0].replace(/-/g, "");

        if (!pdfBase64 || typeof pdfBase64 !== "string") {
          throw new Error("Invalid PDF data");
        }

        await logEvent(
          "Statement Emailed to Tenant",
          `${this.tenantname} (${this.tenantEmail}) – ${this.leaseReference}`,
          "Lease Invoice"
        );

        const emailData = {
          to: this.tenantEmail,
          cc: "rent@gqproperty.co.za",
          subject: `Invoice ${this.leaseReference} ${formattedDate}`,
          text: `Hi ${this.tenantname} Please find your invoice attached.`,
          pdfBase64,
        };

        const response = await fetch("https://sendemail-bqr766ca3q-uc.a.run.app", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(emailData),
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to send invoice: ${errorText}`);
        }

        const result = await response.json();
        alert(result.message);

        await this.saveInvoiceToFirestore(pdfBase64, formattedDate);
        alert("Invoice sent successfully!");
      } catch (error) {
        console.error("Error sending invoice by email:", error);
        alert("An error occurred while sending the invoice.");
      } finally {
        this.loading = false;
      }
    },

    async saveInvoiceToFirestore(pdfBase64, formattedDate) {
      try {
        const invoiceData = {
          leaseReference: this.leaseReference,
          datePaid: new Date().toISOString().split("T")[0],
          description: `Invoice ${this.invoiceNumber}`,
          reference: this.invoiceNumber,
          amountPaid: `-${this.totalDue.replace("R ", "")}`,
          type: "invoice",
          pdfBase64: pdfBase64,
          status: "sent",
          tenantId: this.tenantId,
          propertyId: this.propertyId,
          createdAt: new Date(),
          paymentMethod: "n/a",
          transactionType: "Invoice",
          items: this.sortedEntries.map((entry) => ({
            date: entry.date,
            description: entry.description,
            amount: entry.amount,
            type: entry.type,
          })),
        };

        await addDoc(collection(db, "Invoices"), invoiceData);
      } catch (error) {
        console.error("Error saving invoice to Firestore:", error);
        throw error;
      }
    },
  },

  async created() {
    try {
      await Promise.all([
        this.fetchTransactions(),
        this.fetchInvoiceDetails(),
        this.fetchLogoBase64(),
        this.generateInvoiceNumber()
      ]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      this.loading = false;
    }
  },
};
</script>

<style scoped>
.invoice-container {
  font-family: Arial, sans-serif;
  font-size: 14px;
  color: #333;
  display: flex;
  flex-direction: column;
  width: 210mm;
  padding: 20mm;
  background-color: white;
}

.invoice-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
}

.address-section,
.invoice-info-section {
  font-size: 12px;
  margin: 0;
}

.action-buttons {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin: 15px 0;
}

.buttons-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.logo-section {
  text-align: center;
}

.invoice-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
}

.invoice-table th,
.invoice-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

.invoice-table th {
  background-color: #f2f2f2;
  font-weight: bold;
}

.bank-and-summary {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
}

.bank-details {
  width: 50%;
}

.summary-details {
  text-align: right;
  width: 50%;
}

.bank-details p,
.summary-details p {
  font-size: 12px;
  margin: 5px 0;
}

.tax-invoice {
  font-size: 24px;
  font-weight: bold;
  text-transform: uppercase;
}

.company-logo {
  height: 50px;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
}

.loading-container p {
  margin-top: 10px;
  font-size: 16px;
  color: #333;
}

.edit-icon {
  color: #4caf50;
  cursor: pointer;
}

.delete-icon {
  color: #f44336;
  cursor: pointer;
}

.v-icon {
  font-size: 24px;
}

.aging-section .v-card {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100px;
}

.error--text {
  color: #c62828 !important;
}

.success--text {
  color: #2e7d32 !important;
}
</style>